/**
 * One ↑ / ↓ press on a picked band's or tile section's top edge in the
 * Elevations view (call item #28, PR 4). Pure.
 *
 * A press moves the edge to the next WHOLE inch in its direction, counted up
 * from what the height is measured from (the counter, the tub, the floor) —
 * the grid a drag's Custom height lands on — unless a named line lies on the
 * way: then it stops on that line, so every height a button (or Edit wall's
 * snap list) offers can be reached from the keys. It is held between the
 * limits a drag has. Each caller turns the result into what it saves.
 */

const INCH_M = 0.0254;
/** How far one press moves the edge. There is no coarse step (no Shift ×6):
 * neither the plan nor any arrow-key nudge in the app has one. */
export const NUDGE_STEP_IN = 1;
// A height this close to a whole inch IS that inch (float noise).
const ON_INCH_IN = 1e-4;
// A line this close to the edge is where the edge already is.
const SAME_M = 1e-6;

/**
 * @param {object} a
 * @param {number} a.from where the edge is now (metres)
 * @param {number} a.base what whole inches are counted up from (metres)
 * @param {number} a.min @param {number} a.max the limits (metres)
 * @param {number[]} a.lines the named lines' heights (metres)
 * @param {number} a.direction > 0 up, < 0 down
 * @returns {{at: number, line: number}} where the edge lands, and the index
 *   into `lines` of the line it landed on (-1 for none)
 */
export function nudgeStep({ from, base, min, max, lines = [], direction }) {
  const dir = direction > 0 ? 1 : -1;
  const inches = (from - base) / INCH_M;
  const next =
    dir > 0
      ? Math.floor(inches + ON_INCH_IN) + NUDGE_STEP_IN
      : Math.ceil(inches - ON_INCH_IN) - NUDGE_STEP_IN;
  const target = Math.min(Math.max(base + next * INCH_M, min), max);
  // the nearest line ahead of the edge that the step reaches
  let line = -1;
  lines.forEach((y, i) => {
    const ahead = (y - from) * dir;
    if (ahead <= SAME_M || (target - y) * dir < -SAME_M) return;
    if (line < 0 || ahead < (lines[line] - from) * dir) line = i;
  });
  if (line >= 0) return { at: lines[line], line };
  // Held at a limit, the edge stays where it stood — on its line, if it
  // stood on one (Full height stays Full height, not the inch below it).
  return {
    at: target,
    line: lines.findIndex((y) => Math.abs(y - target) <= SAME_M),
  };
}
